import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useApp } from '@/context/AppContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Users, 
  Calendar, 
  TrendingUp, 
  ChevronRight,
  BookOpen,
  Clock,
  MapPin,
  Flame,
  Building2,
  Plus,
  ArrowRight
} from 'lucide-react';

export const HomePage = () => {
  const { user } = useAuth();
  const { 
    igrejaAtual, 
    celulas, 
    membros, 
    totalCelulas, 
    totalMembros,
    proximosEncontros,
    loadingCelulas,
    loadingMembros 
  } = useApp();

  const stats = [
    { label: 'Células Ativas', value: totalCelulas, icon: Users, color: 'bg-primary', change: '+2 este mês' },
    { label: 'Total de Membros', value: totalMembros, icon: TrendingUp, color: 'bg-blue-500', change: '+5 este mês' },
    { label: 'Encontros Agendados', value: proximosEncontros.length, icon: Calendar, color: 'bg-purple-500', change: 'Próximos 30 dias' },
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  return (
    <div className="page-transition">
      {/* Hero Section */}
      <div className="px-6 lg:px-8 py-12 bg-gradient-to-br from-primary/10 via-background to-secondary/10 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Flame className="w-6 h-6 text-primary" />
                </div>
                <Badge variant="secondary" className="text-xs">
                  {igrejaAtual?.nome || 'Selecione uma igreja'}
                </Badge>
              </div>
              <h1 className="font-heading text-4xl lg:text-5xl font-bold text-foreground">
                Bem-vindo, {user?.nome?.split(' ')[0] || 'Usuário'}!
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl">
                Gerencie suas células, acompanhe os encontros e fortaleça a comunidade de fé.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild>
                <Link to="/celulas">
                  <Users className="w-4 h-4 mr-2" />
                  Ver Células
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/calendario">
                  <Calendar className="w-4 h-4 mr-2" />
                  Calendário
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="card-elevated animate-scale-in" style={{ animationDelay: `${index * 100}ms` }}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className={`p-3 rounded-xl ${stat.color} text-white`}>
                      <stat.icon className="w-6 h-6" />
                    </div>
                    <span className="text-xs text-muted-foreground">{stat.change}</span>
                  </div>
                  <div className="mt-4">
                    {loadingCelulas || loadingMembros ? (
                      <Skeleton className="h-9 w-16" />
                    ) : (
                      <p className="text-3xl font-bold text-foreground">{stat.value}</p>
                    )}
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Content Grid */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Células Section */}
            <div className="lg:col-span-2 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-2xl font-semibold text-foreground">
                  Células
                </h2>
                <Button variant="ghost" asChild>
                  <Link to="/celulas">
                    Ver todas
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
              </div>
              
              {loadingCelulas ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  {[1, 2].map(i => (
                    <Card key={i} className="card-elevated">
                      <CardContent className="p-6">
                        <Skeleton className="h-6 w-3/4 mb-2" />
                        <Skeleton className="h-4 w-full mb-4" />
                        <Skeleton className="h-4 w-1/2" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : celulas.length === 0 ? (
                <Card className="card-elevated">
                  <CardContent className="p-8 text-center">
                    <Flame className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Nenhuma célula encontrada</h3>
                    <p className="text-muted-foreground mb-4">
                      {igrejaAtual 
                        ? 'Comece criando sua primeira célula!' 
                        : 'Selecione ou crie uma igreja primeiro.'}
                    </p>
                    <Button asChild>
                      <Link to="/celulas">
                        <Plus className="w-4 h-4 mr-2" />
                        Criar Célula
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {celulas.slice(0, 4).map((celula, index) => (
                    <Link key={celula.id} to={`/celulas/${celula.id}`}>
                      <Card 
                        className="card-elevated hover:border-primary/50 transition-all duration-300 h-full animate-slide-up"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                              <Flame className="w-5 h-5 text-primary" />
                            </div>
                            <Badge variant="secondary">
                              {celula.dia_semana || 'A definir'}
                            </Badge>
                          </div>
                          <h3 className="font-semibold text-lg text-foreground mb-1">
                            {celula.nome}
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                            {celula.descricao || 'Sem descrição'}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            {celula.horario && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {celula.horario}
                              </span>
                            )}
                            {celula.cidade && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {celula.cidade}
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar - Próximos Encontros */}
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-2xl font-semibold text-foreground">
                  Próximos Encontros
                </h2>
              </div>
              
              <Card className="card-elevated">
                <CardContent className="p-0">
                  {proximosEncontros.length === 0 ? (
                    <div className="p-6 text-center">
                      <Calendar className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">
                        Nenhum encontro agendado
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {proximosEncontros.map((encontro, index) => {
                        const celula = celulas.find(c => c.id === encontro.celula_id);
                        return (
                          <div 
                            key={encontro.id} 
                            className="p-4 hover:bg-muted/50 transition-colors animate-slide-up"
                            style={{ animationDelay: `${index * 50}ms` }}
                          >
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded-lg bg-purple-500/10 text-purple-500">
                                <BookOpen className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground truncate">
                                  {celula?.nome || 'Célula'}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {formatDate(encontro.data)}
                                </p>
                                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                  <Clock className="w-3 h-3" />
                                  {encontro.horario}
                                  {encontro.local && (
                                    <>
                                      <span>•</span>
                                      <MapPin className="w-3 h-3" />
                                      <span className="truncate">{encontro.local}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="card-elevated bg-gradient-to-br from-primary/5 to-primary/10">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-foreground mb-4">Ações Rápidas</h3>
                  <div className="space-y-3">
                    <Button className="w-full justify-start" variant="secondary" asChild>
                      <Link to="/celulas">
                        <Plus className="w-4 h-4 mr-2" />
                        Nova Célula
                      </Link>
                    </Button>
                    <Button className="w-full justify-start" variant="outline" asChild>
                      <Link to="/encontros">
                        <Calendar className="w-4 h-4 mr-2" />
                        Agendar Encontro
                      </Link>
                    </Button>
                    <Button className="w-full justify-start" variant="outline" asChild>
                      <Link to="/membros">
                        <Users className="w-4 h-4 mr-2" />
                        Ver Membros
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="px-6 lg:px-8 py-8 border-t border-border bg-muted/30">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            "Porque onde estiverem dois ou três reunidos em meu nome, aí estou eu no meio deles." 
            <span className="font-medium"> — Mateus 18:20</span>
          </p>
        </div>
      </footer>
    </div>
  );
};
